import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen as BookOpenIcon, Gift as GiftIcon, ShieldCheck as ShieldCheckIcon, Check as CheckIcon, ChevronDown as ChevronDownIcon, Star as StarIcon, X as XIcon } from 'lucide-react';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
const bonusItems = [{
  title: 'Home Auction Deal Scanner Checklist',
  image: 'https://cdn.magicpatterns.com/uploads/oxPzgDkQmKFBvWyrjdSF4Y/Home_auction_deal_scanner_checklist.png',
  hook: 'Find auction homes in your area without wasting hours searching.',
  description: ['Most beginners waste days scrolling around with no real system, and they miss the best deals because someone else spots them first.', 'This simple step-by-step checklist fixes that.', 'It shows you exactly where to look, how to set up smart alerts, and how to spot promising below-market homes faster.', "It's built so you have a repeatable, beginner-friendly system, even if you've never looked at an auction property before."],
  inside: ['A step-by-step checklist to find auction homes in your area', 'How to set up smart alerts so deals come to you automatically', 'What to track so you can spot strong below-market homes fast', 'A repeatable routine you can use every single week'],
  result: 'You know exactly where to look, what to track, and how to catch the best deals early.'
}, {
  title: 'Hidden Risk & Debt Assessment Checklist',
  image: 'https://cdn.magicpatterns.com/uploads/fYi8RmbCujGRDbx3x5VYfD/Hidden_risk__debt_assement_checklist.png',
  hook: 'Spot the warning signs before you bid, so a cheap house never becomes an expensive problem.',
  description: ['A house can look like an amazing deal and still hide debts, liens, or repair problems that cost you a fortune later.', 'This practical checklist helps you avoid those costly mistakes.', 'It walks you through the warning signs to look for before you bid — hidden debts, title issues, repair red flags, and neighborhood concerns.', 'So you can quickly tell the difference between a true great deal and a risky trap.'],
  inside: ['How to check for possible hidden debts and liens', 'The title and paperwork red flags to watch for', 'Repair and condition warning signs that signal trouble', 'Neighborhood and deal-breaker checks before you commit'],
  result: 'You protect your money, avoid nasty surprises, and bid knowing exactly what the risks are.'
}, {
  title: 'Bidding Process Decoded Checklist',
  image: 'https://cdn.magicpatterns.com/uploads/pj9mGqugnG97a2wpJRfDQL/Bidding_process_decoded_checkslit.png',
  hook: 'A calm, clear plan for before, during, and after the auction.',
  description: ["For most beginners, the bidding itself is the scariest part — the rules feel confusing and it's easy to panic or overbid.", 'This beginner-friendly checklist breaks the whole thing into simple, easy-to-follow steps.', 'It shows you exactly what to do before the auction, during the bidding, and right after you win.', 'No confusing rules. Just a calm, clear plan so your first auction feels simple.'],
  inside: ['Exactly what to prepare before the auction starts', 'A simple bidding strategy so you never overpay', 'What to do the moment you win the property', 'The key paperwork and decisions that actually matter'],
  result: 'You feel prepared and calm, and you bid the smart way instead of making expensive mistakes.'
}];
const BASE_PRICE = 6.36;
const BASE_VALUE = 98;
const orderBumps = [{
  id: 'legal-jargon',
  title: 'The Real Affordable Mortgage Checklist',
  price: 9,
  value: 27,
  image: 'https://cdn.magicpatterns.com/uploads/qRWmZGqJLoGKMG27ysC5ij/The-Real-Affordable-Mortgage-Checklist.png',
  label: 'YES — Add The Real Affordable Mortgage Checklist for just $9.',
  headline: "Most First-Time Buyers Guess Their Budget — Don't Be One of Them",
  copy: 'Most beginners look at house prices and just hope the math works out later. This checklist shows you the exact monthly payment you can safely afford — so you never overbid, overpay, or overstretch.',
  bullets: ['Know your real max before you shop', 'Avoid the #1 mistake first-time buyers make', 'Takes less than 10 minutes to complete']
}, {
  id: 'fast-track',
  title: 'The Mortgage & Creative Financing Blueprint',
  price: 9,
  value: 27,
  image: 'https://cdn.magicpatterns.com/uploads/5ZVc1rCDVeyPKYyKmf6A4N/Mortgage-and-Creative-Financing-Blueprint-for-First-Time-Buyers.png',
  label: 'YES — Add The Mortgage & Creative Financing Blueprint for just $9.',
  headline: "Don't Bid on an Auction Home Without Reading This First",
  copy: "Auction deals move fast. If you don't understand your financing options before you bid, you could lose the deal — or worse, get stuck with one you can't close. This beginner-friendly blueprint explains every financing path in simple English so you know exactly how to fund your first home — even through an auction.",
  bullets: ['Understand mortgages without the confusing terms', 'Know which financing fits your situation', 'Be ready before the auction, not after']
}];
export function OfferCTA() {
  const [expanded, setExpanded] = useState(false);
  const [selectedBumps, setSelectedBumps] = useState<Record<string, boolean>>({});
  const toggleBump = (id: string) => setSelectedBumps(prev => ({
    ...prev,
    [id]: !prev[id]
  }));
  const selected = orderBumps.filter(b => selectedBumps[b.id]);
  const totalPrice = BASE_PRICE + selected.reduce((sum, b) => sum + b.price, 0);
  const totalValue = BASE_VALUE + selected.reduce((sum, b) => sum + b.value, 0);
  const savings = totalValue - totalPrice;
  const discountPct = Math.round(savings / totalValue * 100);
  // Shopify permalink based on selected order bumps
  const legalSelected = !!selectedBumps['legal-jargon'];
  const fastSelected = !!selectedBumps['fast-track'];
  let checkoutUrl = 'https://digifactorymax.net/cart/57874198823176:1'; // main product only
  if (legalSelected && fastSelected) {
    checkoutUrl = 'https://digifactorymax.net/cart/57957921947912:1'; // main + both bumps
  } else if (legalSelected) {
    checkoutUrl = 'https://digifactorymax.net/cart/57957929451784:1'; // main + Real Affordable Mortgage
  } else if (fastSelected) {
    checkoutUrl = 'https://digifactorymax.net/cart/57957925355784:1'; // main + Creative Financing Blueprint
  }
  return <Section id="offer" theme="black" className="text-center relative overflow-hidden border-b border-slate-100 bg-slate-50" data-id="element-212">
      {/* Decorative background element */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(#F97316 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-213"></div>

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-214">
        <h2 className="text-3xl md:text-5xl font-display font-bold mb-8 text-slate-900 leading-tight" data-id="element-215">
          The System Was Built to Keep You Renting —{' '}
          <span className="text-orange-500" data-id="element-216">Here's How to Beat It.</span>
        </h2>

        <div className="text-lg md:text-xl text-slate-600 font-sans leading-relaxed mb-16 text-left max-w-2xl mx-auto" data-id="element-217">
          <p className="mb-6" data-id="element-218">
            The game is rigged to keep you renting forever. Let's be honest:
          </p>

          <ul className="space-y-4 mb-8" data-id="element-219">
            <li className="flex gap-3 items-start" data-id="element-220">
              <XIcon className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-221" />
              <span data-id="element-222">
                <strong className="text-slate-900 font-semibold" data-id="element-223">
                  Prices are insane.
                </strong>{' '}
                Everyone fights over the same overpriced listings on the same
                giant apps.
              </span>
            </li>
            <li className="flex gap-3 items-start" data-id="element-224">
              <XIcon className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-225" />
              <span data-id="element-226">
                <strong className="text-slate-900 font-semibold" data-id="element-227">
                  Landlords win, you lose.
                </strong>{' '}
                Your rent pays off their mortgage while you build nothing.
              </span>
            </li>
            <li className="flex gap-3 items-start" data-id="element-228">
              <XIcon className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-229" />
              <span data-id="element-230">
                <strong className="text-slate-900 font-semibold" data-id="element-231">
                  Agents cash in.
                </strong>{' '}
                Big commissions come straight out of your pocket.
              </span>
            </li>
            <li className="flex gap-3 items-start" data-id="element-232">
              <XIcon className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-233" />
              <span data-id="element-234">
                <strong className="text-slate-900 font-semibold" data-id="element-235">
                  Saving 20% feels impossible
                </strong>{' '}
                — and it's designed that way.
              </span>
            </li>
          </ul>

          <p className="text-slate-900 font-bold text-xl md:text-2xl" data-id="element-236">
            There's a smarter way in: home auctions. See everything up front,
            stay in control, and get your first home up to{' '}
            <span className="text-orange-500" data-id="element-237">50% off</span>.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-soft-lg mb-8 mx-auto max-w-2xl border-2 border-orange-500 ring-4 ring-orange-100 overflow-hidden" data-id="element-238">
          <div className="p-10 md:p-14" data-id="element-239">
            {/* Ornamental Divider */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-240">
              <div className="h-1 w-12 bg-orange-500 rounded-full" data-id="element-241"></div>
              <div className="w-2 h-2 rotate-45 bg-orange-300" data-id="element-242"></div>
              <div className="h-1 w-12 bg-orange-500 rounded-full" data-id="element-243"></div>
            </div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-slate-900 leading-[1.15] mb-4 uppercase tracking-tight" data-id="element-244">
              Get Your First House{' '}
              <span className="text-orange-500" data-id="element-245">50% Off</span> With The Secret
              Home Auction Method
            </h3>
            <p className="text-lg sm:text-xl md:text-2xl font-display font-bold text-orange-500 leading-snug mb-8" data-id="element-246">
              It's time to take action and do things differently — buy the
              blueprint today!
            </p>

            {/* What's included — main guide + 3 bonuses recap */}
            <div className="mb-10 text-left" data-id="element-247">
              <p className="text-center text-slate-500 text-sm font-bold uppercase tracking-wider mb-6" data-id="element-248">
                Everything Included Today
              </p>

              <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100" data-id="element-249">
                <div className="flex items-center gap-3" data-id="element-250">
                  <BookOpenIcon className="w-5 h-5 text-orange-500 shrink-0" data-id="element-251" />
                  <span className="text-slate-900 text-sm md:text-base font-semibold" data-id="element-252">
                    The 2026 Home Auction Secrets Blueprint for Beginners
                  </span>
                </div>
                <span className="text-slate-500 text-sm shrink-0 font-medium" data-id="element-253">
                  $47
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-6" data-id="element-254">
                {bonusItems.map(bonus => <div key={bonus.title} className="bg-slate-50 border border-slate-200 p-1.5 rounded-xl" data-id="element-255">
                    <img src={bonus.image} alt={bonus.title} className="w-full h-auto object-contain rounded-lg border border-slate-100" data-id="element-256" />
                  </div>)}
              </div>

              {bonusItems.map((bonus, i) => <div key={bonus.title} className="flex items-center justify-between gap-3 py-3 border-b border-slate-100" data-id="element-257">
                  <div className="flex items-center gap-3" data-id="element-258">
                    <GiftIcon className="w-4 h-4 text-orange-500 shrink-0" data-id="element-259" />
                    <span className="text-slate-600 text-sm md:text-base" data-id="element-260">
                      <span className="text-slate-900 font-semibold" data-id="element-261">
                        Bonus #{i + 1}:
                      </span>{' '}
                      {bonus.title}
                    </span>
                  </div>
                  <span className="text-slate-500 text-sm shrink-0 font-medium" data-id="element-262">
                    $17
                  </span>
                </div>)}

              <div className="flex items-center justify-between gap-3 pt-5" data-id="element-263">
                <span className="text-slate-900 font-display font-bold text-base uppercase tracking-wide" data-id="element-264">
                  Total Value
                </span>
                <span className="text-slate-400 font-display font-bold text-lg line-through" data-id="element-265">
                  ${totalValue}
                </span>
              </div>

              {/* Discount / savings badge */}
              <div className="mt-5 flex items-center justify-center gap-3 bg-green-50 border border-green-200 py-3 px-4 rounded-xl" data-id="element-266">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-500 text-white text-xs font-bold shrink-0" data-id="element-267">
                  %
                </span>
                <span className="text-green-700 font-bold text-sm md:text-base tracking-wide text-center uppercase" data-id="element-268">
                  Save {discountPct}% Today
                </span>
              </div>

              {/* Read more — expandable full bonus detail */}
              <div className="mt-6" data-id="element-269">
                <button onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="group w-full flex items-center justify-center gap-2.5 bg-orange-50 border border-orange-200 hover:border-orange-300 hover:bg-orange-100 text-orange-700 text-sm md:text-base font-bold tracking-wider uppercase py-4 px-6 rounded-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500" data-id="element-270">
                  <GiftIcon className="w-5 h-5 text-orange-500" data-id="element-271" />
                  {expanded ? 'Hide Bonus Details' : 'See Full Details Of All 3 Bonuses'}
                  <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 group-hover:translate-y-0.5 ${expanded ? 'rotate-180' : ''}`} data-id="element-272" />
                </button>

                <AnimatePresence data-id="element-273">
                  {expanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.35,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-274">
                      <div className="space-y-6 pt-6" data-id="element-275">
                        {bonusItems.map((bonus, i) => <div key={bonus.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6" data-id="element-276">
                            <div className="flex items-center gap-3 mb-3" data-id="element-277">
                              <div className="bg-orange-100 p-1.5 rounded-lg" data-id="element-278">
                                <GiftIcon className="w-4 h-4 text-orange-500 shrink-0" data-id="element-279" />
                              </div>
                              <span className="text-orange-600 text-xs font-bold tracking-wider uppercase" data-id="element-280">
                                Free Bonus #{i + 1}
                              </span>
                            </div>

                            <h4 className="text-slate-900 font-display font-bold text-lg md:text-xl mb-3 leading-tight" data-id="element-281">
                              {bonus.title}
                            </h4>

                            <p className="text-slate-800 font-bold text-base mb-4 border-l-4 border-orange-500 pl-3 bg-white py-2 rounded-r-lg" data-id="element-282">
                              "{bonus.hook}"
                            </p>

                            <div className="space-y-3 text-slate-600 text-sm leading-relaxed mb-5" data-id="element-283">
                              {bonus.description.map((p, j) => <p key={j} data-id="element-284">{p}</p>)}
                            </div>

                            <div className="mb-5 bg-white p-4 rounded-xl border border-slate-100" data-id="element-285">
                              <p className="text-slate-900 font-bold text-xs uppercase tracking-wider mb-3" data-id="element-286">
                                Inside you'll get:
                              </p>
                              <ul className="space-y-2" data-id="element-287">
                                {bonus.inside.map((item, j) => <li key={j} className="flex gap-3 items-start" data-id="element-288">
                                    <CheckIcon className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" data-id="element-289" />
                                    <span className="text-slate-600 text-sm" data-id="element-290">
                                      {item}
                                    </span>
                                  </li>)}
                              </ul>
                            </div>

                            <div className="pt-3 border-t border-slate-200" data-id="element-291">
                              <p className="text-orange-600 font-bold text-xs uppercase tracking-wider mb-1" data-id="element-292">
                                The Result:
                              </p>
                              <p className="text-slate-900 font-medium text-sm leading-relaxed" data-id="element-293">
                                {bonus.result}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex justify-center items-start mb-12" data-id="element-294">
              <span className="text-3xl font-display font-bold text-orange-500 mt-2" data-id="element-295">
                $
              </span>
              <span className="text-7xl md:text-8xl font-display font-bold text-orange-500 tracking-tighter" data-id="element-296">
                {totalPrice.toFixed(2)}
              </span>
            </div>

            <Button fullWidth variant="green" checkoutUrl={checkoutUrl} pixelValue={totalPrice} className="md:px-14 py-5 text-base sm:text-lg md:text-xl font-bold uppercase tracking-wide mb-3" data-id="element-297">
              Buy Now — ${totalPrice.toFixed(2)}
            </Button>

            {/* Order Bumps */}
            <div className="space-y-4 mt-6 mb-6 text-left" data-id="element-298">
              {orderBumps.map(bump => {
              const checked = !!selectedBumps[bump.id];
              return <motion.div key={bump.id} onClick={() => toggleBump(bump.id)} animate={checked ? {
                boxShadow: '0 0 0px 0px rgba(249,115,22,0)'
              } : {
                boxShadow: ['0 0 0px 0px rgba(249,115,22,0.0)', '0 0 16px 1px rgba(249,115,22,0.15)', '0 0 0px 0px rgba(249,115,22,0.0)']
              }} transition={{
                duration: 2.6,
                repeat: checked ? 0 : Infinity,
                ease: 'easeInOut'
              }} className={`relative cursor-pointer border-2 border-dashed p-4 pt-7 rounded-2xl transition-colors duration-200 text-left ${checked ? 'border-orange-500 bg-orange-50' : 'border-slate-300 bg-white hover:border-orange-300'}`} data-id="element-299">
                    {/* Highly Recommended badge */}
                    <div className="absolute -top-3 left-4 inline-flex items-center gap-1.5 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm" data-id="element-300">
                      <StarIcon className="w-3 h-3 fill-white" data-id="element-301" />
                      Highly Recommended
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-302">
                      <span className={`mt-0.5 flex items-center justify-center w-5 h-5 shrink-0 border rounded transition-colors ${checked ? 'bg-orange-500 border-orange-500' : 'bg-white border-slate-300'}`} data-id="element-303">
                        {checked && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-304" />}
                        <input type="checkbox" checked={checked} onChange={() => toggleBump(bump.id)} className="sr-only" aria-label={bump.label} data-id="element-305" />
                      </span>
                      <span className="text-slate-900 font-bold text-sm md:text-base leading-snug uppercase tracking-wide" data-id="element-306">
                        {bump.label}
                      </span>
                    </label>

                    <div className="flex flex-col items-start text-left gap-4 mt-4" data-id="element-307">
                      <img src={bump.image} alt={bump.title} className="w-full h-auto object-contain rounded-xl border border-slate-200 shadow-sm" data-id="element-308" />
                      {bump.headline && <p className="text-slate-900 font-display font-bold text-base leading-snug" data-id="element-309">
                          {bump.headline}
                        </p>}
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-310">
                        {bump.copy}
                      </p>
                      {bump.bullets && <ul className="space-y-2 w-full" data-id="element-311">
                          {bump.bullets.map(b => <li key={b} className="flex gap-2.5 items-start" data-id="element-312">
                              <CheckIcon className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" data-id="element-313" />
                              <span className="text-slate-700 text-sm" data-id="element-314">
                                {b}
                              </span>
                            </li>)}
                        </ul>}
                    </div>
                  </motion.div>;
            })}
            </div>

            <div className="flex items-center justify-center gap-2 mb-8 px-2" data-id="element-315">
              <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-316">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" data-id="element-317"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" data-id="element-318"></span>
              </span>
              <p className="text-slate-900 text-xs sm:text-sm font-bold tracking-wide uppercase text-center leading-snug line-clamp-2" data-id="element-319">
                Special Offer — Limited Promotion. Will End Soon.
              </p>
            </div>

            <div className="flex flex-col items-center gap-3" data-id="element-320">
              <div className="text-slate-600 text-sm flex items-center gap-2 font-medium" data-id="element-321">
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-322">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" data-id="element-323"></path>
                </svg>
                Instant PDF Download
              </div>
              <div className="text-slate-500 text-xs" data-id="element-324">
                Start reading on your phone in the next 2 minutes.
              </div>
            </div>

            {/* 7-Day Guarantee */}
            <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col items-center text-center" data-id="element-325">
              <div className="bg-orange-50 p-4 rounded-full mb-4" data-id="element-326">
                <ShieldCheckIcon className="w-10 h-10 text-orange-500" strokeWidth={2} data-id="element-327" />
              </div>
              <h4 className="text-slate-900 font-display font-bold text-lg md:text-xl mb-2" data-id="element-328">
                100% 7-Day Guarantee
              </h4>
              <p className="text-slate-600 text-sm md:text-base max-w-md leading-relaxed" data-id="element-329">
                If you don't love it, just email us within 7 days and we'll give
                you every penny back. No questions asked.
              </p>
            </div>

            {/* Final Order Summary + Buy Button */}
            <div className="mt-10 pt-8 border-t border-slate-200 text-left" data-id="element-330">
              <p className="text-center text-orange-600 text-xs font-bold tracking-wider uppercase mb-6" data-id="element-331">
                Your Order Summary
              </p>

              <div className="space-y-0 bg-slate-50 rounded-2xl p-6 border border-slate-200" data-id="element-332">
                <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-200" data-id="element-333">
                  <div className="flex items-center gap-3" data-id="element-334">
                    <BookOpenIcon className="w-4 h-4 text-orange-500 shrink-0" data-id="element-335" />
                    <span className="text-slate-900 text-sm md:text-base font-semibold" data-id="element-336">
                      The 2026 Home Auction Secrets Blueprint for Beginners
                    </span>
                  </div>
                  <span className="text-slate-500 text-sm shrink-0 font-medium" data-id="element-337">
                    $47
                  </span>
                </div>

                {bonusItems.map((bonus, i) => <div key={bonus.title} className="flex items-center justify-between gap-3 py-3 border-b border-slate-200" data-id="element-338">
                    <div className="flex items-center gap-3" data-id="element-339">
                      <GiftIcon className="w-4 h-4 text-orange-500 shrink-0" data-id="element-340" />
                      <span className="text-slate-600 text-sm md:text-base" data-id="element-341">
                        <span className="text-slate-900 font-semibold" data-id="element-342">
                          Bonus #{i + 1}:
                        </span>{' '}
                        {bonus.title}
                      </span>
                    </div>
                    <span className="text-green-600 text-xs font-bold uppercase shrink-0 bg-green-100 px-2 py-1 rounded-md" data-id="element-343">
                      Free
                    </span>
                  </div>)}

                {selected.map(bump => <div key={bump.id} className="flex items-center justify-between gap-3 py-3 border-b border-slate-200" data-id="element-344">
                    <div className="flex items-center gap-3" data-id="element-345">
                      <CheckIcon className="w-4 h-4 text-orange-500 shrink-0" data-id="element-346" />
                      <span className="text-slate-600 text-sm md:text-base" data-id="element-347">
                        <span className="text-slate-900 font-semibold" data-id="element-348">
                          Add-On:
                        </span>{' '}
                        {bump.title}
                      </span>
                    </div>
                    <span className="text-slate-500 text-sm shrink-0 font-medium" data-id="element-349">
                      ${bump.price}
                    </span>
                  </div>)}

                <div className="flex items-center justify-between gap-3 pt-5" data-id="element-350">
                  <span className="text-slate-900 font-display font-bold text-lg uppercase tracking-wide" data-id="element-351">
                    Total Today
                  </span>
                  <span className="text-orange-600 font-display font-bold text-2xl" data-id="element-352">
                    ${totalPrice.toFixed(2)}
                  </span>
                </div>

                <p className="text-green-600 text-sm font-bold text-right mt-1" data-id="element-353">
                  Save {discountPct}% off today
                </p>
              </div>

              <Button fullWidth variant="green" checkoutUrl={checkoutUrl} pixelValue={totalPrice} className="md:px-14 py-5 text-base sm:text-lg md:text-xl font-bold uppercase tracking-wide mt-8" data-id="element-354">
                Buy Now — ${totalPrice.toFixed(2)}
              </Button>

              <div className="text-slate-500 text-xs text-center mt-4 flex items-center justify-center gap-2 font-medium" data-id="element-355">
                <ShieldCheckIcon className="w-4 h-4 text-orange-500" data-id="element-356" />
                Secure checkout · Instant access · 7-day guarantee
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>;
}